/* factory-layered-renderer.js - Canvas 2D layered renderer helpers for the factory view. */
(function () {
  const Manifest = window.FactoryGraphicsManifest || {};
  const Loader = window.FactoryGraphicsLoader || {};
  const Adapters = window.FactoryAnimationAdapters || {};
  const LAYER_ORDER = Manifest.LAYER_ORDER || ["base", "machines", "actors", "foreground", "lighting", "qa"];

  function object(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function array(value) {
    return Array.isArray(value) ? value : [];
  }

  function cleanString(value, fallback) {
    const clean = String(value || "").trim();
    return clean || fallback || "";
  }

  function sceneFrom(manifest) {
    return manifest && manifest.scene || Manifest.DEFAULT_SCENE || { width: 1420, height: 700, tileW: 52, tileH: 26, originX: 530, originY: 76 };
  }

  function isoPoint(scene, x, y) {
    return {
      x: Number(scene.originX) + (Number(x) - Number(y)) * Number(scene.tileW) / 2,
      y: Number(scene.originY) + (Number(x) + Number(y)) * Number(scene.tileH) / 2,
    };
  }

  function drawContactShadow(ctx, x, y, width, height, options) {
    if (!ctx) return;
    const opts = object(options);
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(Number(opts.alpha ?? 0.22) || 0.22, 1));
    ctx.fillStyle = cleanString(opts.fill, "rgba(0,0,0,0.34)");
    ctx.beginPath();
    ctx.ellipse(Number(x) || 0, Number(y) || 0, Math.max(2, Number(width) || 28), Math.max(2, Number(height) || 8), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function drawRoundRect(ctx, x, y, width, height, radius) {
    if (ctx.roundRect) {
      ctx.roundRect(x, y, width, height, radius);
      return;
    }
    const r = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
  }

  function drawMachinePart(ctx, part, origin, time, options) {
    if (!ctx || !part) return;
    const opts = object(options);
    const x = Number(origin.x) + Number(part.x || 0);
    const y = Number(origin.y) + Number(part.y || 0);
    const alpha = Math.max(0, Math.min(Number(part.alpha ?? 1) || 1, 1));
    const phase = Number(time) || 0;
    ctx.save();
    ctx.globalAlpha = alpha * Math.max(0, Math.min(Number(opts.opacity ?? 1) || 1, 1));
    ctx.fillStyle = cleanString(part.fill, "#7f7f7f");
    ctx.strokeStyle = cleanString(part.stroke, "rgba(35,30,25,0.42)");
    ctx.lineWidth = Math.max(1, Number(opts.lineWidth) || 1);
    if (part.animation === "spin") {
      ctx.translate(x, y);
      ctx.rotate(phase * Math.PI * 2);
      ctx.translate(-x, -y);
    } else if (part.animation === "press") {
      ctx.translate(0, Math.sin(phase * Math.PI * 2) * 5);
    } else if (part.animation === "slide") {
      ctx.translate(Math.sin(phase * Math.PI * 2) * 4, 0);
    }
    if (part.kind === "circle") {
      ctx.beginPath();
      ctx.arc(x, y, Math.max(1, Number(part.r) || 8), 0, Math.PI * 2);
      ctx.fill();
      if (part.stroke) ctx.stroke();
    } else if (part.kind === "line") {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Number(part.w || 0), y + Number(part.h || 0));
      ctx.stroke();
    } else {
      const width = Number(part.w || 24);
      const height = Number(part.h || 16);
      ctx.beginPath();
      drawRoundRect(ctx, x - width / 2, y - height / 2, width, height, Math.min(6, Math.abs(height) / 3));
      ctx.fill();
      if (part.stroke) ctx.stroke();
    }
    ctx.restore();
  }

  function machineOrigin(scene, machine, fallback) {
    const tiles = array(machine && machine.tiles);
    if (!tiles.length) return { x: Number(fallback && fallback.x) || 0, y: Number(fallback && fallback.y) || 0 };
    const avg = tiles.reduce((sum, tile) => ({ x: sum.x + (Number(tile[0]) || 0), y: sum.y + (Number(tile[1]) || 0) }), { x: 0, y: 0 });
    const point = isoPoint(scene, avg.x / tiles.length + Number(machine.anchor && machine.anchor.x || 0), avg.y / tiles.length + Number(machine.anchor && machine.anchor.y || 0));
    return point;
  }

  function buildMachineParts(type, options) {
    const clean = cleanString(type, "machine").toLowerCase();
    const opts = object(options);
    if (Array.isArray(opts.parts) && opts.parts.length) return opts.parts;
    if (clean === "press") {
      return [
        { id: "press-shadow", kind: "rect", layer: "base", x: 0, y: 7, w: 88, h: 18, fill: "rgba(0,0,0,0.18)", alpha: 1 },
        { id: "press-frame", kind: "rect", layer: "machines", x: 0, y: -28, w: 78, h: 62, fill: "#7b6b54", stroke: "#4d4236" },
        { id: "press-bed", kind: "rect", layer: "machines", x: 0, y: 4, w: 92, h: 20, fill: "#5f5246", stroke: "#3b332d" },
        { id: "press-ram", kind: "rect", layer: "machines", x: 0, y: -37, w: 42, h: 20, fill: "#b48a4d", stroke: "#6e5130", animation: "press" },
        { id: "press-wheel", kind: "circle", layer: "foreground", x: -42, y: -44, r: 15, fill: "#6d786e", stroke: "#3d443f", animation: "spin" },
      ];
    }
    if (clean === "lathe") {
      return [
        { id: "lathe-shadow", kind: "rect", layer: "base", x: 0, y: 6, w: 96, h: 16, fill: "rgba(0,0,0,0.16)", alpha: 1 },
        { id: "lathe-bed", kind: "rect", layer: "machines", x: 0, y: -6, w: 96, h: 18, fill: "#67716c", stroke: "#3f4743" },
        { id: "lathe-head", kind: "rect", layer: "machines", x: -36, y: -24, w: 26, h: 34, fill: "#76664d", stroke: "#4b4133" },
        { id: "lathe-carriage", kind: "rect", layer: "machines", x: 15, y: -18, w: 30, h: 26, fill: "#a47c45", stroke: "#60482d", animation: "slide" },
        { id: "lathe-wheel", kind: "circle", layer: "foreground", x: 38, y: -28, r: 10, fill: "#6d786e", stroke: "#3d443f", animation: "spin" },
      ];
    }
    return [
      { id: `${clean}-shadow`, kind: "rect", layer: "base", x: 0, y: 6, w: 72, h: 14, fill: "rgba(0,0,0,0.16)", alpha: 1 },
      { id: `${clean}-body`, kind: "rect", layer: "machines", x: 0, y: -20, w: 64, h: 42, fill: "#78736a", stroke: "#4b4640" },
    ];
  }

  function drawLayeredMachine(ctx, machine, options) {
    if (!ctx || !machine) return;
    const opts = object(options);
    const scene = opts.scene || sceneFrom(opts.manifest);
    const origin = opts.origin || machineOrigin(scene, machine, opts);
    const phase = ((Number(opts.time) || 0) * (Number(opts.speed) || 0.45)) % 1;
    const parts = buildMachineParts(machine.type || machine.id, { parts: machine.parts });
    const layer = cleanString(opts.layer, "");
    parts.forEach((part) => {
      if (layer && cleanString(part.layer, "machines") !== layer) return;
      if (part.id && part.id.includes("shadow")) drawContactShadow(ctx, origin.x + Number(part.x || 0), origin.y + Number(part.y || 0), Math.abs(Number(part.w || 48)) / 2, Math.abs(Number(part.h || 12)) / 2, { alpha: part.alpha ?? 0.16, fill: part.fill });
      else drawMachinePart(ctx, part, origin, phase, opts);
    });
  }

  function collectOccluders(manifest, entities) {
    const scene = sceneFrom(manifest);
    const machineOccluders = Object.keys(object(manifest && manifest.machines)).map((key) => {
      const machine = manifest.machines[key];
      const origin = machineOrigin(scene, machine);
      const occluder = object(machine.occluder);
      return {
        id: machine.id || key,
        type: "machine",
        x: origin.x + (Number(occluder.x) || 0),
        y: origin.y + (Number(occluder.y) || -36),
        width: Number(occluder.width || occluder.w) || 72,
        height: Number(occluder.height || occluder.h) || 56,
        depth: Number(occluder.depth) || origin.y,
        source: machine,
      };
    });
    const entityOccluders = array(entities).filter((entity) => entity && entity.occluder).map((entity) => ({
      id: entity.id || entity.key || "entity",
      type: "entity",
      x: Number(entity.x) + (Number(entity.occluder.x) || 0),
      y: Number(entity.y) + (Number(entity.occluder.y) || 0),
      width: Number(entity.occluder.width || entity.occluder.w) || Number(entity.width) || 32,
      height: Number(entity.occluder.height || entity.occluder.h) || Number(entity.height) || 48,
      depth: Number(entity.depth ?? entity.y) || 0,
      source: entity,
    }));
    return machineOccluders.concat(entityOccluders).sort((a, b) => a.depth - b.depth);
  }

  function drawQaOverlay(ctx, renderer, state) {
    if (!ctx || !renderer || !(renderer.quality && renderer.quality.qaOverlay)) return;
    const manifest = renderer.manifest || {};
    const scene = sceneFrom(manifest);
    ctx.save();
    ctx.strokeStyle = "rgba(30,80,160,0.22)";
    ctx.fillStyle = "rgba(30,80,160,0.55)";
    ctx.font = "11px system-ui, sans-serif";
    for (let y = 0; y < 22; y += 1) {
      for (let x = 0; x < 34; x += 1) {
        const p = isoPoint(scene, x, y);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y - scene.tileH / 2);
        ctx.lineTo(p.x + scene.tileW / 2, p.y);
        ctx.lineTo(p.x, p.y + scene.tileH / 2);
        ctx.lineTo(p.x - scene.tileW / 2, p.y);
        ctx.closePath();
        ctx.stroke();
      }
    }
    collectOccluders(manifest, state && state.entities).forEach((occluder) => {
      ctx.strokeStyle = "rgba(180,70,30,0.65)";
      ctx.strokeRect(occluder.x - occluder.width / 2, occluder.y - occluder.height, occluder.width, occluder.height);
      ctx.fillText(occluder.id, occluder.x - occluder.width / 2, occluder.y - occluder.height - 3);
    });
    ctx.restore();
  }

  class FactoryLayeredRenderer {
    constructor(canvasOrContext, runtime, options) {
      this.canvas = canvasOrContext && canvasOrContext.canvas ? canvasOrContext.canvas : canvasOrContext || null;
      this.ctx = canvasOrContext && canvasOrContext.canvas ? canvasOrContext : this.canvas && this.canvas.getContext ? this.canvas.getContext("2d") : null;
      this.runtime = runtime || {};
      this.manifest = this.runtime.manifest || object(options && options.manifest);
      this.bindings = this.runtime.bindings || object(options && options.bindings);
      this.quality = this.runtime.quality || (Loader.getQuality ? Loader.getQuality() : {});
      this.hooks = object(options && options.hooks);
      this.adapters = new Map();
    }

    setRuntime(runtime) {
      this.runtime = runtime || this.runtime || {};
      this.manifest = this.runtime.manifest || this.manifest;
      this.bindings = this.runtime.bindings || this.bindings;
      this.quality = this.runtime.quality || this.quality;
    }

    setQuality(quality) {
      this.quality = Manifest.mergeQualitySettings ? Manifest.mergeQualitySettings(this.quality, quality) : { ...this.quality, ...object(quality) };
    }

    setQaOverlay(enabled) {
      this.setQuality({ qaOverlay: !!enabled });
    }

    clear(ctx) {
      const target = ctx || this.ctx;
      const scene = sceneFrom(this.manifest);
      if (target) target.clearRect(0, 0, scene.width, scene.height);
    }

    withLayer(ctx, layer, draw) {
      const definition = object(this.manifest && this.manifest.layers && this.manifest.layers[layer]);
      if (definition.visible === false) return;
      ctx.save();
      ctx.globalCompositeOperation = cleanString(definition.blendMode, "source-over");
      const opacity = Number(definition.opacity ?? 1);
      ctx.globalAlpha *= Math.max(0, Math.min(Number.isFinite(opacity) ? opacity : 1, 1));
      if (this.hooks.beforeLayer) this.hooks.beforeLayer(ctx, layer, this);
      draw();
      if (this.hooks.afterLayer) this.hooks.afterLayer(ctx, layer, this);
      ctx.restore();
    }

    drawLayerBackground(ctx, layer) {
      const definition = object(this.manifest && this.manifest.layers && this.manifest.layers[layer]);
      const scene = sceneFrom(this.manifest);
      let drewImage = false;
      if (definition.fill) {
        ctx.fillStyle = definition.fill;
        ctx.fillRect(0, 0, scene.width, scene.height);
      }
      const imageKey = cleanString(definition.image, "");
      const image = imageKey && this.runtime.getImage ? this.runtime.getImage(imageKey) : Loader.getCachedImage ? Loader.getCachedImage(imageKey, this.manifest) : null;
      if (image) {
        ctx.drawImage(image, 0, 0, scene.width, scene.height);
        drewImage = true;
      }
      else if (imageKey && this.runtime.loadImage) this.runtime.loadImage(imageKey);
      return drewImage;
    }

    drawBaseLayer(ctx, state) {
      const scene = sceneFrom(this.manifest);
      const drewImage = this.drawLayerBackground(ctx, "base");
      if (state && state.drawFloor) {
        state.drawFloor(ctx, scene);
        return;
      }
      if (drewImage) return;
      ctx.fillStyle = "#d7c79a";
      ctx.fillRect(0, 0, scene.width, scene.height);
      ctx.strokeStyle = "rgba(96,82,55,0.22)";
      for (let y = 0; y < 22; y += 1) {
        for (let x = 0; x < 34; x += 1) {
          const p = isoPoint(scene, x, y);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y - scene.tileH / 2);
          ctx.lineTo(p.x + scene.tileW / 2, p.y);
          ctx.lineTo(p.x, p.y + scene.tileH / 2);
          ctx.lineTo(p.x - scene.tileW / 2, p.y);
          ctx.closePath();
          ctx.stroke();
        }
      }
    }

    adapterFor(entity) {
      const item = object(entity);
      const key = cleanString(item.adapterKey || item.animation || item.kind || item.type || item.id, "actor-placeholder");
      if (this.adapters.has(key)) return this.adapters.get(key);
      const animation = this.manifest && this.manifest.animations && (this.manifest.animations[key] || this.manifest.animations[item.animation] || this.manifest.animations["actor-placeholder"]);
      const adapter = Adapters.createAdapter ? Adapters.createAdapter(animation || { id: key, type: "placeholder" }, this.runtime) : null;
      if (adapter) this.adapters.set(key, adapter);
      return adapter;
    }

    drawEntity(ctx, entity, state) {
      const item = object(entity);
      if (item.visible === false) return;
      if ((this.quality && this.quality.shadows) !== false) drawContactShadow(ctx, Number(item.x) || 0, Number(item.y) || 0, Number(item.shadowWidth || 18), Number(item.shadowHeight || 6), item.shadow);
      const adapter = this.adapterFor(item);
      if (adapter) {
        adapter.play(item.action || item.state || "idle");
        adapter.update(Number(state && state.deltaSeconds) || 0);
        adapter.draw(ctx, item, { imageSmoothing: this.quality && this.quality.imageSmoothing !== false });
      }
    }

    drawMachines(ctx, state, layer) {
      const machines = object(this.manifest && this.manifest.machines);
      Object.keys(machines).forEach((key) => drawLayeredMachine(ctx, machines[key], {
        manifest: this.manifest,
        scene: sceneFrom(this.manifest),
        layer,
        time: state && state.time,
      }));
    }

    drawForegroundLayer(ctx, state) {
      this.drawLayerBackground(ctx, "foreground");
      this.drawMachines(ctx, state, "foreground");
    }

    drawLightingLayer(ctx, state) {
      if (this.quality && this.quality.lighting === false) return;
      this.drawLayerBackground(ctx, "lighting");
      const scene = sceneFrom(this.manifest);
      const lights = array(this.manifest && this.manifest.lighting && this.manifest.lighting.lamps);
      if (!lights.length) {
        const gradient = ctx.createLinearGradient(0, 0, 0, scene.height);
        gradient.addColorStop(0, "rgba(255,245,210,0.18)");
        gradient.addColorStop(1, "rgba(45,38,30,0.10)");
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, scene.width, scene.height);
        return;
      }
      lights.forEach((light) => {
        const p = light.tile ? isoPoint(scene, light.tile[0], light.tile[1]) : { x: Number(light.x) || 0, y: Number(light.y) || 0 };
        const radius = Number(light.radius) || 90;
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
        gradient.addColorStop(0, cleanString(light.color, "rgba(255,232,166,0.30)"));
        gradient.addColorStop(1, "rgba(255,232,166,0)");
        ctx.fillStyle = gradient;
        ctx.fillRect(p.x - radius, p.y - radius, radius * 2, radius * 2);
      });
    }

    render(state) {
      const ctx = this.ctx;
      if (!ctx) return;
      const scene = sceneFrom(this.manifest);
      ctx.save();
      ctx.imageSmoothingEnabled = !(this.quality && this.quality.imageSmoothing === false);
      ctx.clearRect(0, 0, scene.width, scene.height);
      const drawState = object(state);
      this.withLayer(ctx, "base", () => this.drawBaseLayer(ctx, drawState));
      this.withLayer(ctx, "machines", () => this.drawMachines(ctx, drawState, "machines"));
      this.withLayer(ctx, "actors", () => array(drawState.entities).slice().sort((a, b) => (Number(a.depth ?? a.y) || 0) - (Number(b.depth ?? b.y) || 0)).forEach((entity) => this.drawEntity(ctx, entity, drawState)));
      this.withLayer(ctx, "foreground", () => this.drawForegroundLayer(ctx, drawState));
      this.withLayer(ctx, "lighting", () => this.drawLightingLayer(ctx, drawState));
      this.withLayer(ctx, "qa", () => drawQaOverlay(ctx, this, drawState));
      ctx.restore();
    }

    dispose() {
      this.adapters.forEach((adapter) => { if (adapter && adapter.dispose) adapter.dispose(); });
      this.adapters.clear();
    }
  }

  const api = {
    FactoryLayeredRenderer,
    isoPoint,
    drawContactShadow,
    collectOccluders,
    drawQaOverlay,
    buildMachineParts,
    drawMachinePart,
    drawLayeredMachine,
    machineOrigin,
  };

  window.FactoryLayeredRenderer = FactoryLayeredRenderer;
  window.FactoryLayeredRendererHelpers = api;
  window.FactoryGraphics = { ...(window.FactoryGraphics || {}), renderer: api };
})();
